import { watch, type Ref } from 'vue'
import type { Project } from '@/core/types/api'
import { logger } from '@/core/utils/logger'

interface ProjectScopedChangeContext {
  project: Project
  previousProject?: Project | null
  projectCode: string
}

interface ProjectScopedResetContext {
  project?: Project | null
  previousProject?: Project | null
}

interface UseProjectScopedEntitiesOptions {
  project: Ref<Project | null | undefined>
  onProjectChange?: (context: ProjectScopedChangeContext) => void | Promise<void>
  onProjectUnset?: (context: ProjectScopedResetContext) => void | Promise<void>
  reset?: (context: ProjectScopedResetContext) => void
  immediate?: boolean
}

export function useProjectScopedEntities(options: UseProjectScopedEntitiesOptions) {
  const {
    project,
    onProjectChange,
    onProjectUnset,
    reset,
    immediate = true
  } = options

  const stop = watch(project, async (newProject, oldProject) => {
    if (newProject?.id === oldProject?.id) {
      return
    }

    const context: ProjectScopedResetContext = {
      project: newProject ?? null,
      previousProject: oldProject ?? null
    }

    if (!newProject?.id) {
      reset?.(context)
      try {
        await onProjectUnset?.(context)
      } catch (error: unknown) {
        logger.error('onProjectUnset failed', error)
      }
      return
    }

    reset?.(context)

    if (onProjectChange) {
      try {
        await onProjectChange({
          project: newProject,
          previousProject: oldProject ?? null,
          projectCode: newProject.code
        })
      } catch (error: unknown) {
        logger.error('onProjectChange failed', error)
      }
    }
  }, { immediate })

  const ensureProjectCode = () => project.value?.code ?? null

  return {
    currentProject: project,
    ensureProjectCode,
    stop
  }
}
